import {
  AdminClassroomDocument,
  AdminClassroomStudentsDocument,
  ClassroomStudentsDocument,
  type ClassroomStudentsQuery,
  MyClassroomDocument,
} from "@/generated/graphql";
import { useQuery } from "@apollo/client/react";
import type { ClassroomScope } from "./classroomScope";

export type RosterStudent = ClassroomStudentsQuery["classroomStudents"][number];

/**
 * Null until the name is known, which the pages render as a skeleton rather than a wrong title.
 * Asked for by id rather than found in the teacher's own list, which holds back the classrooms
 * this app is not sold into and would leave such a page titleless for good.
 */
export function useClassroomName(scope: ClassroomScope, classroomId: string): string | null {
  const isAdmin = scope === "admin";
  const teacherQuery = useQuery(MyClassroomDocument, {
    variables: { classroomId },
    skip: isAdmin,
  });
  const adminQuery = useQuery(AdminClassroomDocument, {
    variables: { id: classroomId },
    skip: !isAdmin,
  });

  if (isAdmin) return adminQuery.data?.adminClassroom?.name ?? null;
  return teacherQuery.data?.myClassroom.name ?? null;
}

/** The school a synced classroom belongs to, so admin pages can point back at its siblings. */
export function useAdminClassroomSchool(
  scope: ClassroomScope,
  classroomId: string
): { id: string; name: string } | null {
  const { data } = useQuery(AdminClassroomDocument, {
    variables: { id: classroomId },
    skip: scope !== "admin",
  });
  const classroom = data?.adminClassroom;
  return classroom ? { id: classroom.schoolId, name: classroom.schoolName } : null;
}

export function useClassroomRoster(scope: ClassroomScope, classroomId: string) {
  const isAdmin = scope === "admin";
  const teacherQuery = useQuery(ClassroomStudentsDocument, {
    variables: { classroomId },
    skip: isAdmin,
  });
  const adminQuery = useQuery(AdminClassroomStudentsDocument, {
    variables: { classroomId },
    skip: !isAdmin,
  });

  const active = isAdmin ? adminQuery : teacherQuery;
  const students: RosterStudent[] = isAdmin
    ? (adminQuery.data?.adminClassroomStudents ?? [])
    : (teacherQuery.data?.classroomStudents ?? []);

  return {
    students,
    loading: active.loading,
    error: active.error,
    refetch: () => {
      void (isAdmin ? adminQuery.refetch() : teacherQuery.refetch());
    },
  };
}
